import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";

import { toast } from "react-toastify";
import openSocket from "../../services/socket-io";

import ContactDrawer from "../ContactDrawer";
import CarrinhoDaConversa from "../CarrinhoDaConversa";
import MessageInput from "../MessageInput/";
import TicketHeader from "../TicketHeader";
import TicketInfo, { TicketTagsBar } from "../TicketInfo";
import ChipDeExpediente from "../ChipDeExpediente";
import useEstadoDeExpediente from "../../hooks/useEstadoDeExpediente";
import TagSelect from "../TagSelect";
import TicketActionButtons from "../TicketActionButtons";
import MessagesList from "../MessagesList";
import AvisoDePausa from "../AvisoDePausa";
import TicketMediaGallery from "../TicketMediaGallery";
import { Button } from "../ui/button";
import { Files, PlugZap } from "lucide-react";
import api from "../../services/api";
import { ReplyMessageProvider } from "../../context/ReplyingMessage/ReplyingMessageContext";
import toastError from "../../errors/toastError";
import useTecladoVirtual from "../../hooks/useTecladoVirtual";
import useWhatsApps from "../../hooks/useWhatsApps";
import usePrefTickets from "../../hooks/usePrefTickets";
import { ACOES_PADRAO } from "../TicketActionButtons/acoesDaBarra";
import {
  ticketFaz,
  conexaoDoTicket,
  conexaoForaDoAr,
} from "../../helpers/capacidadesDoCanal";

const Ticket = () => {
  const [acoesBarra] = usePrefTickets("acoesBarra", ACOES_PADRAO);
  const [painelContatoAberto] = usePrefTickets("painelContatoAberto", false);
  const [etiquetasNoCabecalho] = usePrefTickets("etiquetasNoCabecalho", true);
  const { ticketId } = useParams();
  const navigate = useNavigate();

  // UM painel lateral por vez ('vendas' | 'contato' | null), em qualquer
  // largura: os dois booleanos de antes deixavam Vendas e Contato abertos
  // juntos, empilhados no mesmo z-index abaixo de 1280px — fechar um revelava
  // o outro. Abrir o painel de contato junto com a conversa só vale de xl
  // para cima: abaixo disso ele é uma camada por cima da conversa inteira.
  const [painelLateral, setPainelLateral] = useState(() =>
    painelContatoAberto && window.matchMedia("(min-width: 1280px)").matches
      ? "contato"
      : null,
  );
  const [galeriaAberta, setGaleriaAberta] = useState(false);
  const [loading, setLoading] = useState(true);

  // Uma busca só para o app inteiro (cache no hook): o cabeçalho remonta a
  // cada troca de conversa, e uma chamada por atendimento aberto seria uma
  // ida ao servidor por clique na lista.
  const expedientes = useEstadoDeExpediente();
  const [contact, setContact] = useState({});
  const [ticket, setTicket] = useState({});
  // Reagir não existe em todo canal (Messenger e Direct declaram
  // `reactions: false`): sem perguntar, a tela oferece o emoji e o backend
  // recusa com 422 na cara de quem atende.
  const { whatsApps } = useWhatsApps();

  // Conexão caída: a tela do atendimento nunca dizia nada, e quem atendia
  // digitava, dava enter e acreditava que tinha falado com o cliente. O status
  // já chegava pelo `useWhatsApps` (é o que acende a bolinha no menu); só
  // faltava cruzá-lo com o `whatsappId` do atendimento.
  const conexao = conexaoDoTicket(whatsApps, ticket);
  const foraDoAr = conexaoForaDoAr(conexao);

  // Conexão de reserva desta conexão, para a bolha de falha oferecer "enviar
  // por ela". Só quando a atual está fora do ar e a reserva está no ar: propor
  // trocar o número que fala com o cliente por qualquer outro motivo seria
  // convidar a bagunça. Em grupo, nunca — o outro número não é membro dele, e
  // o backend recusa; oferecer o botão seria prometer o que não acontece.
  const reserva = (whatsApps || []).find(
    (w) => w.id === conexao?.failoverWhatsappId && w.status === "CONNECTED",
  );

  // Sem espera artificial: abrir conversa é um clique, não digitação. O meio
  // segundo que havia aqui atrasava o cabeçalho de toda abertura, e a lista de
  // mensagens esperava outro meio segundo por conta dela.
  //
  // A troca rápida de conversa continua protegida: quem responde por último
  // não manda na tela — quem manda é o `ticketId` que ainda está aberto.
  useEffect(() => {
    let atual = true;

    setLoading(true);

    const fetchTicket = async () => {
      try {
        const { data } = await api.get("/tickets/" + ticketId);

        if (!atual) return;

        setContact(data.contact);
        setTicket(data);
        setLoading(false);
      } catch (err) {
        if (!atual) return;

        setLoading(false);
        toastError(err);
      }
    };

    fetchTicket();

    return () => {
      atual = false;
    };
  }, [ticketId, navigate]);

  // Participantes do grupo: o nome no lugar do `@<digitos>` das menções e a
  // lista do `@` no campo. Falhar aqui só deixa o código à vista.
  // Guardada com o `ticketId` de onde veio: trocando de conversa, a lista do
  // grupo anterior não vale enquanto a nova não chega.
  const [participantesDe, setParticipantesDe] = useState({});
  const grupo =
    participantesDe.ticketId === ticketId
      ? participantesDe
      : { participantes: [], podeMencionar: false };

  useEffect(() => {
    if (!ticket.isGroup || String(ticket.id) !== String(ticketId)) return;

    let atual = true;

    api
      .get(`/tickets/${ticketId}/participantes`)
      .then(({ data }) => atual && setParticipantesDe({ ...data, ticketId }))
      .catch(() => {});

    return () => {
      atual = false;
    };
  }, [ticketId, ticket.id, ticket.isGroup]);

  useEffect(() => {
    const socket = openSocket();

    socket.on("connect", () => socket.emit("joinChatBox", ticketId));

    socket.on("ticket", (data) => {
      // Mesma armadilha do MessagesList: o evento "ticket" também vai para as
      // salas de status e de notificação, e a conexão do Socket.IO é uma só
      // para o app inteiro. Sem conferir o id, qualquer mexida em OUTRO
      // atendimento (alguém aceitar, transferir, encerrar) trocava o
      // cabeçalho e os botões desta tela pelos do ticket alheio — e o
      // "delete", que o UpdateTicketService dispara a cada mudança de status,
      // expulsava quem estava lendo a conversa com um "Ticket deleted".
      if (data.action === "update") {
        if (Number(data.ticket?.id) !== Number(ticketId)) return;
        setTicket(data.ticket);
      }

      // `motivo: "status"` é o ticket saindo de uma lista (foi aceito,
      // transferido, encerrado) — continua existindo, e quem está lendo não
      // pode ser expulso da conversa por isso.
      if (data.action === "delete" && data.motivo !== "status") {
        if (Number(data.ticketId) !== Number(ticketId)) return;
        toast.success("Atendimento apagado.");
        navigate("/tickets");
      }
    });

    socket.on("contact", (data) => {
      if (data.action === "update") {
        setContact((prevState) => {
          if (prevState.id === data.contact?.id) {
            return { ...prevState, ...data.contact };
          }
          return prevState;
        });
      }
    });

    // A conexão caiu e voltou (ou a aba ficou de lado): o atendimento pode ter
    // sido aceito, transferido ou encerrado nesse meio-tempo, e o cabeçalho
    // mostraria a situação de antes. Busca em silêncio, sem o carregando.
    let ativo = true;

    socket.on("resync", () => {
      api
        .get("/tickets/" + ticketId)
        .then(({ data }) => {
          if (!ativo) return;
          setTicket(data);
          setContact(data.contact);
        })
        .catch(() => {});
    });

    return () => {
      ativo = false;
      socket.disconnect();
    };
  }, [ticketId, navigate]);

  // Ao digitar no celular o teclado cobre a parte de baixo da tela, mas o
  // `h-dvh` do layout continua contando a tela inteira: a caixa de digitar
  // ficava atrás do teclado e o cabeçalho saía por cima. Amarrar a altura
  // desta tela à faixa visível devolve o comportamento de aplicativo de
  // mensagem — cabeçalho colado no topo, caixa de texto colada no teclado e
  // só a conversa rolando no meio.
  const { alturaVisivel, deslocamento, tecladoAberto } = useTecladoVirtual();

  const estiloTeclado = tecladoAberto
    ? {
        height: `${alturaVisivel}px`,
        // O navegador empurra a página para cima para revelar o campo focado;
        // sem devolver esse deslocamento o cabeçalho fica fora da tela.
        transform: `translateY(${deslocamento}px)`,
      }
    : undefined;

  // Encolher a área visível não move o rolamento da conversa: a última
  // mensagem, que estava no fim, some atrás da caixa de texto. Recolar no fim
  // quando o teclado abre (e a cada mudança de altura) mantém à vista o que a
  // pessoa estava lendo.
  useEffect(() => {
    if (!tecladoAberto) return;
    const lista = document.getElementById("messagesList");
    if (lista) lista.scrollTop = lista.scrollHeight;
  }, [tecladoAberto, alturaVisivel]);

  const handleDrawerOpen = () => {
    setPainelLateral("contato");
  };

  const handleDrawerClose = () => {
    setPainelLateral(null);
  };

  return (
    <div
      id="drawer-container"
      className="relative flex h-full w-full overflow-hidden"
      style={estiloTeclado}
    >
      <div className="flex h-full min-w-0 flex-1 flex-col overflow-hidden bg-background">
        <TicketHeader loading={loading}>
          <div className="flex min-w-0 flex-1 items-center gap-2">
            <TicketInfo
              contact={contact}
              ticket={ticket}
              onClick={handleDrawerOpen}
            />
            {/* Fora do expediente: informativo, e fora do botão do TicketInfo
                porque botão dentro de botão é HTML inválido. Explica por que o
                cliente recebeu o aviso automático — responder continua
                liberado. */}
            {!loading && ticket.id ? (
              <ChipDeExpediente estado={expedientes[ticket.whatsappId]} />
            ) : null}
          </div>
          {/* Etiquetas: mais um ícone da barra, ao lado de Arquivos. Fora do
              TicketInfo porque ele é um <button>, e botão dentro de botão é
              HTML inválido — o clique de um engole o do outro. */}
          {etiquetasNoCabecalho && !loading && ticket.id && (
            <TicketTagsBar ticket={ticket} contact={contact}>
              <TagSelect />
            </TicketTagsBar>
          )}
          {/* Atalho para as imagens da conversa. Fica antes das ações porque
              é leitura — não muda o estado do atendimento. */}
          {acoesBarra.includes("arquivos") && (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setGaleriaAberta(true)}
              title="Arquivos do atendimento"
              aria-label="Arquivos do atendimento"
              className="shrink-0"
            >
              <Files className="h-5 w-5" />
            </Button>
          )}
          <TicketActionButtons ticket={ticket} />
        </TicketHeader>

        {foraDoAr && (
          <div
            role="status"
            className="flex flex-none items-start gap-2 border-b border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive"
          >
            <PlugZap className="mt-0.5 h-4 w-4 shrink-0" />
            <p>
              A conexão <strong>{conexao?.name}</strong> está fora do ar
              {conexao?.status === "qrcode" || conexao?.status === "PAIRING"
                ? " (esperando leitura do QR Code)"
                : ""}
              ; a mensagem não vai sair até ela voltar.
            </p>
          </div>
        )}

        <ReplyMessageProvider>
          <MessagesList
            ticketId={ticketId}
            isGroup={ticket.isGroup}
            participantes={grupo.participantes}
            podeReagir={ticketFaz(whatsApps, ticket, "reactions")}
            podeEditar={ticketFaz(whatsApps, ticket, "edicao")}
            mostrarRecibos={ticketFaz(whatsApps, ticket, "recibos")}
            reserva={foraDoAr && !ticket.isGroup ? reserva : null}
          ></MessagesList>
          <AvisoDePausa ticket={ticket} />
          <MessageInput
            ticketStatus={ticket.status}
            contactId={contact?.id}
            contact={contact}
            whatsappId={ticket.whatsappId}
            vendaAguardando={ticket.vendaAguardando}
            carrinhoAberto={painelLateral === "vendas"}
            onAbrirCarrinho={() =>
              setPainelLateral((p) => (p === "vendas" ? null : "vendas"))
            }
            conexaoForaDoAr={foraDoAr}
            nomeDaConexao={conexao?.name}
            participantes={grupo.podeMencionar ? grupo.participantes : []}
          />
        </ReplyMessageProvider>
      </div>
      <TicketMediaGallery
        open={galeriaAberta}
        onClose={() => setGaleriaAberta(false)}
        ticketId={ticketId}
      />
      <CarrinhoDaConversa
        open={painelLateral === "vendas"}
        onClose={() => setPainelLateral(null)}
        contact={contact}
        ticket={ticket}
      />
      <ContactDrawer
        open={painelLateral === "contato"}
        handleDrawerClose={handleDrawerClose}
        contact={contact}
        ticket={ticket}
        loading={loading}
      />
    </div>
  );
};

export default Ticket;
